import type { ReactNode } from 'react';
import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './TagColored.css';

export type TagColoredVariant =
  | 'pink'
  | 'burgundy'
  | 'purple'
  | 'blue'
  | 'cyan'
  | 'lime'
  | 'volcano'
  | 'peach'
  | 'yellow'
  | 'disabled';

/** Color-coded label tag with optional prefix/suffix icons. Available in 9 color variants plus disabled. */
export type TagColoredProps = {
  label?: ReactNode;
  variant?: TagColoredVariant;
  showPrefixIcon?: boolean;
  prefixIconSlug?: string;
  /** Decorative icon after the label (not a delete control). Inherits the tag text color. */
  endIconSlug?: string;
  showSuffixIcon?: boolean;
  /**
   * When true, removes all padding and the fixed height so the tag flows
   * inline with surrounding text (inherits font size and line height).
   * Used for inline entity highlighting inside text samples.
   */
  noPadding?: boolean;
  onDelete?: () => void;
  className?: string;
};

export function TagColored({
  label = 'Tag',
  variant = 'pink',
  showPrefixIcon = false,
  prefixIconSlug = 'plus',
  endIconSlug,
  showSuffixIcon = false,
  noPadding = false,
  onDelete,
  className = '',
}: TagColoredProps) {
  const isDisabled = variant === 'disabled';

  return (
    <span
      className={[
        'spark-tag-colored',
        `spark-tag-colored-${variant}`,
        noPadding ? 'spark-tag-colored-no-padding' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showPrefixIcon ? <SparkIcon slug={prefixIconSlug} size="small" className="spark-tag-colored-icon" /> : null}
      <span className="spark-tag-colored-label">{label}</span>
      {endIconSlug ? <SparkIcon slug={endIconSlug} size="small" className="spark-tag-colored-icon" /> : null}
      {showSuffixIcon ? (
        <TooltipTrigger text={typeof label === 'string' ? `Remove ${label}` : 'Remove'} disabled={isDisabled}>
          <button
            type="button"
            className="spark-tag-colored-delete"
            aria-label={typeof label === 'string' ? `Remove ${label}` : 'Remove'}
            disabled={isDisabled}
            onClick={onDelete}
          >
            <SparkIcon slug="trash" size="small" className="spark-tag-colored-icon" />
          </button>
        </TooltipTrigger>
      ) : null}
    </span>
  );
}
